<header class="go-page-title">
  <h1 class="go-heading-1">{{ pageTitle }}</h1>
</header>
<section class="go-container">
  <go-card class="go-column go-column--50">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap" id="basic-forms">Basic Forms</h2>
      <go-copy cardId="basic-forms" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-container">
        <div class="go-column go-column--100">
          <p class="go-copy">
            All forms follow several simple rules.
          </p>
          <ol class="go-ordered-list">
            <li class="go-ordered-list__item">
              Be sure to use an appropriate type attribute on all inputs.
            </li>
            <li class="go-ordered-list__item">
              All inputs should also have a corresponding label.
            </li>
            <li class="go-ordered-list__item">
              Outlines on inputs should never be hidden as to have better
              visibility and accessibility.
            </li>
            <li class="go-ordered-list__item">
              Outlines should also correspond consistently with error states.
            </li>
            <li class="go-ordered-list__item">
              Hints are to be used as help text and as a way to display more
              robust validation errors.
            </li>
            <li class="go-ordered-list__item">
              UI controls should never apply brand colors.
            </li>
          </ol>
        </div>
      </div>
      <form class="go-form">
        <div class="go-container go-container--form">
          <div class="go-column go-column--50">
            <label for="first-name-input" class="go-form__label">First Name</label>
            <input class="go-form__input" id="first-name-input" placeholder="Jonny" type="text">
          </div>
          <div class="go-column go-column--50">
            <label for="last-name-input" class="go-form__label">Last Name</label>
            <input class="go-form__input" id="last-name-input" placeholder="Appleseed" type="text">
          </div>
          <div class="go-column go-column--100">
            <label for="email-input" class="go-form__label">Email</label>
            <input class="go-form__input" id="email-input" placeholder="your@email.com" type="email">
          </div>
          <div class="go-column go-column--100">
            <label for="password-input" class="go-form__label">Password</label>
            <input class="go-form__input" id="password-input" placeholder="**************" type="password">
          </div>
          <div class="go-column go-column--100 go-column--no-padding">
            <go-button
              buttonVariant="positive"
              buttonIcon="done"
            >
              Submit
            </go-button>
          </div>
        </div>
      </form>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50" [showHeader]="false">
    <ng-container go-card-content>
      <code [highlight]="basicForm"></code>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap" id="form-hints">Form Hints</h2>
      <go-copy cardId="form-hints" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-container">
        <div class="go-column go-column--100">
          <p class="go-body-copy">
            Form hints exist to help give more information about the inputs they
            are attached to. They should be directly preceded by the input in
            which they describe.
          </p>
          <p class="go-body-copy">
            Hints have two states. The default state is established by the
            <code class="code-block--inline">go-hint</code> class.
          </p>
        </div>
      </div>
      <form class="go-form">
        <div class="go-container go-container--form">
          <div class="go-column go-column--100">
            <label for="phone-input-hint" class="go-form__label">Phone Number</label>
            <input class="go-form__input" id="phone-input-hint" placeholder="+0(000)000-0000">
            <p class="go-hint">
              This is the phone number for your work phone.
              Please use the following format: +0(000)000-0000.
              If you have any further questions please contact support.
            </p>
          </div>
          <div class="go-column go-column--100">
           <p class="go-body-copy">
              If more information is needed to describe an input in an error state
              the <code class="code-block--inline">go-hint--error</code> modifier
              class can be additionally added to make the errors more prominent.
            </p>
            <p class="go-body-copy">
              For better accessibility in the error state, an additional 
              <code class="code-block--inline">go-hint__status</code>
              element can be added directly inside the hint to better
              distinguish between different types of hints.
            </p>
          </div>
          <div class="go-column go-column--100">
            <label for="phone-input-error" class="go-form__label">Phone Number</label>
            <input class="go-form__input go-form__input--error" id="phone-input-error" placeholder="+0(000)000-0000">
            <p class="go-hint go-hint--error">
              <span class="go-hint__status">Error:</span>
              Phone number is a required input.
            </p>
            <p class="go-hint">
              This is the phone number for your work phone.
              Please use the following format: +0(000)000-0000.
              If you have any further questions please contact support.
            </p>
          </div>
        </div>
      </form>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50" [showHeader]="false">
    <ng-container go-card-content>
      <code [highlight]="formHints"></code>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap" id="text-modifiers">Text Input Modifiers</h2>
      <go-copy cardId="text-modifiers" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-container">
        <div class="go-column go-column--100">
          <p class="go-body-copy">All inputs have three basic states.</p>
          <p class="go-body-copy">
            The default state is established by the <code class="code-block--inline">go-form__input</code>
            class on text based inputs.
          </p>
          <p class="go-body-copy">
            The disabled state can be achieved by the addition of the
            <code class="code-block--inline">disabled</code> attribute,
            while the error state can be applied by the addition of the
            <code class="code-block--inline">go-form__input--error</code>
            modifier class.
          </p>
          <p class="go-body-copy">
            An input with an error modifier should be followed by an error hint describing
            why it is in such a state.
          </p>
          <p class="go-body-copy">
            If there is an instance where an individual label or input
            needs to be displayed on top of a dark background the
            <code class="code-block--inline">--dark</code> modifiers
            can be added.
          </p>
          <ol class="go-list go-list--ordered">
            <li>
              <code class="code-block--inline">.go-form__label--dark</code>
              - To apply the dark theme styles to a label
            </li>
            <li>
              <code class="code-block--inline">.go-form__input--dark</code>
              - To apply the dark theme styles to an input
            </li>
          </ol>
        </div>
      </div>
      <form class="go-form">
        <div class="go-container go-container--form">
          <div class="go-column go-column--100">
            <label for="email-input" class="go-form__label">Normal Input</label>
            <input class="go-form__input" id="normal-input" placeholder=".go-form__input" type="text">
          </div>
          <div class="go-column go-column--100">
            <label for="email-input" class="go-form__label">Disabled Input</label>
            <input class="go-form__input" id="disabled-input" placeholder=".go-form__input:disabled" type="text" disabled>
          </div>
          <div class="go-column go-column--100">
            <label for="email-input" class="go-form__label">Error Input</label>
            <input class="go-form__input go-form__input--error" id="error-input" placeholder=".go-form__input.go-form__input--error" type="text">
            <p class="go-hint go-hint--error">
              <span class="go-hint__status">Error:</span>
              This input is required. Please fill this out to proceed.
            </p>
          </div>
          <div class="go-column go-column--100 go-column--dark">
            <label for="dark-input" class="go-form__label go-form__label--dark">Dark Input <em>(.go-form__label.go-form__label--dark)</em></label>
            <input class="go-form__input go-form__input--dark" id="dark-input" placeholder=".go-form__input.go-form__input--dark" type="text">
          </div>
        </div>
      </form>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50" [showHeader]="false">
    <ng-container go-card-content>
      <code [highlight]="inputModifiers"></code>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap" id="select-modifiers">Select Box Modifiers</h2>
      <go-copy cardId="select-modifiers" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-container">
        <div class="go-column go-column--100">
          <p class="go-body-copy">
            Similar to the input element, the default state of select boxes are established
            by the <code class="code-block--inline">go-form__select</code> class.
          </p>
          <p class="go-body-copy">
            The disabled state can be achieved by the addition of the
            <code class="code-block--inline">disabled</code>
            attribute, while the error state can be applied by the addition of the
            <code class="code-block--inline">go-form__select--error</code>
            modifier class.
          </p>
          <p class="go-body-copy">
            A select box with an error modifier should be followed by an error hint
            describing why it is in such a state.
          </p>
          <p class="go-body-copy">
            If there is an instance where an individual label or select
            box needs to be displayed on top of a dark background the
            <code class="code-block--inline">--dark</code> modifiers
            can be added.
          </p>
          <ol class="go-list go-list--ordered">
            <li>
              <code class="code-block--inline">.go-form__label--dark</code>
              - To apply the dark theme styles to a label
            </li>
            <li>
              <code class="code-block--inline">.go-form__select--dark</code>
              - To apply the dark theme styles to the select box
            </li>
          </ol>
        </div>
      </div>
      <form class="go-form">
        <div class="go-container go-container--form">
          <div class="go-column go-column--100">
            <label for="normal-select" class="go-form__label">Normal Select</label>
            <select class="go-form__select" id="normal-select">
              <option value="" disabled selected>.go-form__select</option>
              <option value="1">Option #1</option>
              <option value="2">Option #2</option>
            </select>
          </div>
          <div class="go-column go-column--100">
            <label for="disabled-select" class="go-form__label">Disabled Select</label>
            <select class="go-form__select" id="disabled-select" disabled>
              <option value="" disabled selected>.go-form__select:disabled</option>
              <option value="1">Option #1</option>
              <option value="2">Option #2</option>
            </select>
          </div>
          <div class="go-column go-column--100">
            <label for="error-select" class="go-form__label">Error Select</label>
            <select class="go-form__select go-form__select--error" id="error-select">
              <option value="" disabled selected>.go-form__select.go-form__select--error</option>
              <option value="1">Option #1</option>
              <option value="2">Option #2</option>
            </select>
            <p class="go-hint go-hint--error">
              <span class="go-hint__status">Error:</span>
              This input is required. Please select an option to proceed.
            </p>
          </div>
          <div class="go-column go-column--100 go-column--dark">
            <label for="dark-select" class="go-form__label go-form__label--dark">
              Dark Select
              <em>(.go-form__label.go-form__label--dark)</em>
            </label>
            <select class="go-form__select go-form__select--dark" id="dark-select">
              <option value="" disabled selected>.go-form__select.go-form__select--dark</option>
              <option value="1">Option #1</option>
              <option value="2">Option #2</option>
            </select>
          </div>
        </div>
      </form>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50" [showHeader]="false">
    <ng-container go-card-content>
      <code [highlight]="selectModifiers"></code>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap" id="checkbox-modifiers">Check Box / Radio Button Modifiers</h2>
      <go-copy cardId="checkbox-modifiers" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-container">
        <div class="go-column go-column--100">
          <p class="go-body-copy">
            Similar to the input element, the default state of check boxes and radio buttons
            are established by the <code class="code-block--inline">go-form__checkbox</code>
            class.
          </p>
          <p class="go-body-copy">
            These types of inputs often appear in groups and should always be wrapped in a
            <code class="code-block--inline">fieldset</code> element with an additional
            <code class="code-block--inline">legend</code> element describing the choice
            that is being made.
          </p>
          <p class="go-body-copy">
            The disabled state can be achieved by the addition of the
            <code class="code-block--inline">disabled</code>
            attribute to the parent
            <code class="code-block--inline">fieldset</code>
            , while the error state can be applied by the addition of the
            <code class="code-block--inline">go-form__select--error</code>
            modifier class to the same element.
          </p>
          <p class="go-body-copy">
            A input with an error modifier should be followed by an error hint
            describing why it is in such a state.
          </p>
          <p class="go-body-copy">
            If there is an instance where an individual label or fieldset
            needs to be displayed on top of a dark background the
            <code class="code-block--inline">--dark</code> modifiers
            can be added.
          </p>
          <ol class="go-list go-list--ordered">
            <li>
              <code class="code-block--inline">.go-form__label--dark</code>
              - To apply the dark theme styles to a label
            </li>
            <li>
              <code class="code-block--inline">.go-form__fieldset--dark</code>
              - To apply the dark theme styles to the fieldset
            </li>
          </ol>
        </div>
        <div class="go-column go-column--100 go-column--no-padding">
          <form class="go-form">
            <div class="go-container go-container--form">
              <div class="go-column go-column--100">
                <fieldset class="go-form__fieldset">
                  <legend class="go-form__legend">Select a Default Option</legend>
                  <div>
                    <input class="go-form__checkbox" id="default-checkbox-1" type="checkbox" checked>
                    <label for="default-checkbox-1" class="go-form__label go-form__label--inline">
                      Option #1
                    </label>
                  </div>
                  <div>
                    <input class="go-form__checkbox" id="default-checkbox-2" type="checkbox">
                    <label for="default-checkbox-2" class="go-form__label go-form__label--inline">
                      Option #2
                    </label>
                  </div>
                </fieldset>

                <fieldset class="go-form__fieldset" disabled>
                  <legend class="go-form__legend">Select a Disabled Option</legend>
                  <div>
                    <input class="go-form__checkbox" id="disabled-checkbox-1" type="checkbox" checked>
                    <label for="disabled-checkbox-1" class="go-form__label go-form__label--inline">
                      Option #1
                    </label>
                  </div>
                  <div>
                    <input class="go-form__checkbox" id="disabled-checkbox-2" type="checkbox">
                    <label for="disabled-checkbox-2" class="go-form__label go-form__label--inline">
                      Option #2
                    </label>
                  </div>
                </fieldset>

                <fieldset class="go-form__fieldset go-form__fieldset--error go-form__fieldset--no-margin">
                  <legend class="go-form__legend">Select an Error Option</legend>
                  <div>
                    <input class="go-form__checkbox" id="error-checkbox-1" type="checkbox" checked>
                    <label for="error-checkbox-1" class="go-form__label go-form__label--inline">
                      Option #1
                    </label>
                  </div>
                  <div>
                    <input class="go-form__checkbox" id="error-checkbox-2" type="checkbox">
                    <label for="error-checkbox-2" class="go-form__label go-form__label--inline">
                      Option #2
                    </label>
                    <p class="go-hint go-hint--error">
                      <span class="go-hint__status">Error:</span>
                      This input is required. Please select an option to proceed.
                    </p>
                  </div>
                </fieldset>
              </div>
              <div class="go-column go-column--100 go-column--dark">
                <fieldset class="go-form__fieldset go-form__fieldset--dark go-form__fieldset--no-margin">
                  <legend class="go-form__legend go-form__legend--dark">Select a dark option</legend>
                  <div>
                    <input class="go-form__checkbox go-form__checkbox--dark" id="dark-checkbox-1" type="checkbox" checked>
                    <label for="dark-checkbox-1" class="go-form__label go-form__label--dark go-form__label--inline">
                      #1 .go-form__label.go-form__label--dark
                    </label>
                  </div>
                  <div>
                    <input class="go-form__checkbox go-form__checkbox--dark" id="dark-checkbox-2" type="checkbox">
                    <label for="dark-checkbox-2" class="go-form__label go-form__label--dark go-form__label--inline">
                      #2 .go-form__label.go-form__label--dark
                    </label>
                  </div>
                </fieldset>
              </div>
            </div>
          </form>
        </div>
      </div>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50" [showHeader]="false">
    <ng-container go-card-content>
      <code [highlight]="checkboxModifiers"></code>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50" id="dark-forms">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap">Dark Forms</h2>
      <go-copy cardId="dark-forms" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <p class="go-body-copy">
        Dark forms are mostly like the default forms with a few
        alterations based on the state of the input. Labels and
        text show up as a light color so they are legible on a
        dark background. When the input is in a disabled state,
        the input will darken slightly to better portray itself
        as such.
      </p>

      <h3 class="go-heading-3">Implementation</h3>
      <p class="go-body-copy">
        The dark form effect can be achieved in one of two ways.
        Generally speaking we will have a whole form that we need
        to make dark. In this case, we can simply add the
        <code class="code-block--inline">.go-form--dark</code>
        modifier class to the surrounding form element.
      </p>
      <p class="go-body-copy">
        There might be cases, however, where individual inputs are
        not contained within a form element. The dark input styles
        can be applied to individual elements by adding the
        <code class="code-block--inline">--dark</code>
        modifier to any individual element as well. See documentation
        above for individual examples of how to implement dark forms.
      </p>

      <h3 class="go-heading-3">Example</h3>
      <p class="go-body-copy">
        One of the most common use cases of a dark form is when forms
        are being displayed in the off canvas.
      </p>
      <go-button
        (handleClick)="openOffCanvas()"
        buttonIcon="subdirectory_arrow_right"
        buttonVariant="neutral"
      >
        Open Off Canvas
      </go-button>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--50" [showHeader]="false">
    <ng-container go-card-content>
      <code [highlight]="darkForm"></code>
    </ng-container>
  </go-card>

  <go-card class="go-column go-column--100 go-column--no-padding">
    <ng-container go-card-header>
      <h2 class="go-heading-2 go-heading--no-wrap" id="advanced-form-example">
        Advanced Form Example
      </h2>
      <go-copy cardId="advanced-form-example" goCopyCardLink></go-copy>
    </ng-container>
    <ng-container go-card-content>
      <div class="go-container">
        <div class="go-column go-column--100">
          <p class="go-body-copy">
            Below is an example of all of the pieces working together.
            This includes a good example of how to use the grid system
            in conjunction with the form inputs to create a more robust layout.
          </p>
        </div>
      </div>
      <form class="go-form">
        <div class="go-container go-container--form">
          <div class="go-column go-column--50 go-column--no-padding">
            <div class="go-container go-container--form">
              <div class="go-column go-column--50">
                <label for="advanced-first-name" class="go-form__label">First Name</label>
                <input class="go-form__input" id="advanced-first-name" placeholder="Jonny" type="text">
              </div>
              <div class="go-column go-column--50">
                <label for="advanced-last-name" class="go-form__label">Last Name</label>
                <input class="go-form__input" id="advanced-last-name" placeholder="Appleseed" type="text">
              </div>
              <div class="go-column go-column--100">
                <label for="advanced-phone" class="go-form__label">Phone Number</label>
                <input class="go-form__input" id="advanced-phone" placeholder="+0(000)000-0000">
                <p class="go-hint">
                  This is the phone number for your work phone. 
                  Please use the following format: +0(000)000-0000.
                  If you have any further questions please contact support.
                </p>
              </div>
              <div class="go-column go-column--100">
                <label for="advanced-email" class="go-form__label">Email</label>
                <input class="go-form__input" id="advanced-email" placeholder="your@email.com" type="email">
              </div>
              <div class="go-column go-column--100">
                <label for="advanced-password" class="go-form__label">Password</label>
                <input class="go-form__input" id="advanced-password" placeholder="**************" type="password">
              </div>
              <div class="go-column go-column--100">
                <label for="advanced-bio" class="go-form__label">Personal Bio</label>
                <textarea 
                  class="go-form__input"
                  id="advanced-bio"
                  placeholder="Tell us a little bit about yourself.">
                </textarea>
              </div>
            </div>
          </div>

          <div class="go-column go-column--50 go-column--no-padding">
            <fieldset class="go-form__fieldset">
              <legend class="go-form__legend">Employee Information</legend>
              <div class="go-container  go-container--form">
                <div class="go-column go-column--100">
                  <label for="advanced-employee-id" class="go-form__label">Employee ID</label>
                  <input
                    class="go-form__input"
                    id="advanced-employee-id"
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxxx"
                    type="text">
                </div>
                <div class="go-column go-column--100">
                  <label for="country-input" class="go-form__label">Country</label>
                  <select class="go-form__select" id="country-input">
                    <option value="" disabled selected>Select A Country</option>
                    <option value="1">United States</option>
                    <option value="2">Canada</option>
                  </select>
                  <p class="go-hint">
                    This setting specifies the country in which the shipping 
                    address is located.
                  </p>
                </div>

                <div class="go-column go-column--100 go-column--no-padding">
                  <fieldset class="go-form__fieldset">
                    <legend class="go-form__legend">Select A Favorite Character</legend>
                    <div>
                      <input
                        class="go-form__checkbox"
                        id="advanced-radio-1"
                        name="character"
                        type="radio">
                      <label for="advanced-radio-1" class="go-form__label go-form__label--inline">
                        Draco Malfoy
                      </label>
                    </div>
                    <div>
                      <input
                        class="go-form__checkbox"
                        id="advanced-radio-2"
                        name="character"
                        type="radio">
                      <label for="advanced-radio-2" class="go-form__label go-form__label--inline">
                        The Owl
                      </label>
                    </div>
                    <div>
                      <input
                        class="go-form__checkbox"
                        id="advanced-radio-3"
                        name="character"
                        type="radio">
                      <label for="advanced-radio-3" class="go-form__label go-form__label--inline">
                        Harry Potter
                      </label>
                    </div>
                    <p class="go-hint">
                      These are all characters from the beloved Harry Potter series
                      written by J.R.R. Tolkien.
                    </p>
                  </fieldset>
                </div>
                <div class="go-column go-column--100 go-column--no-padding">
                  <fieldset class="go-form__fieldset go-form__fieldset--no-margin">
                    <legend>Best Golden Girl?</legend>
                    <div class="go-container go-container--form">
                      <div class="go-column go-column--50 go-column--no-padding">
                        <div>
                          <input
                            class="go-form__checkbox"
                            id="advanced-checkbox-1"
                            name="character"
                            type="checkbox"
                            checked>
                          <label for="advanced-checkbox-1" class="go-form__label go-form__label--inline">
                            Rose
                          </label>
                        </div>
                        <div>
                          <input
                            class="go-form__checkbox"
                            id="advanced-checkbox-2"
                            name="character"
                            type="checkbox"
                            checked>
                          <label for="advanced-checkbox-2" class="go-form__label go-form__label--inline">
                            Blanche
                          </label>
                        </div>
                      </div>
                      <div class="go-column go-column--50 go-column--no-padding">
                        <div>
                          <input
                            class="go-form__checkbox"
                            id="advanced-checkbox-3"
                            name="character"
                            type="checkbox"
                            checked>
                          <label for="advanced-checkbox-3" class="go-form__label go-form__label--inline">
                            Sophia
                          </label>
                        </div>
                        <div>
                          <input
                            class="go-form__checkbox"
                            id="advanced-checkbox-4"
                            name="character"
                            type="checkbox"
                            checked>
                          <label for="advanced-checkbox-4" class="go-form__label go-form__label--inline">
                            Dorothy
                          </label>
                        </div>
                      </div>
                    </div>
                  </fieldset>
                </div>
              </div>
            </fieldset>
          </div>

          <div class="go-column go-column--100">
            <div>
              <go-button buttonVariant="button">Submit</go-button>
            </div>
          </div>
        </div>
      </form>

      <div class="go-container">
        <div class="go-column go-column--100 go-column--no-padding">
          <code [highlight]="advancedForm"></code>
        </div>
      </div>
    </ng-container>
  </go-card>
</section>
